import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { getArticles } from '../services/api';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BookOpen, ArrowRight, ArrowLeft } from 'lucide-react';
import { ContentContext } from '../App';

const BlogListPage = () => {
  const { language } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [articlesRes] = await Promise.all([
          getArticles()
        ]);
        setArticles(articlesRes.data);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    // Fetch content for header/footer
    import('../services/api').then(({ getWebsiteContent }) => {
      getWebsiteContent().then(res => setContent(res.data));
    });
  }, []);

  return (
    <ContentContext.Provider value={content}>
      <div className="min-h-screen bg-slate-50">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              {language === 'en' ? 'Roofing Knowledge Center' : '屋顶知识中心'}
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              {language === 'en' 
                ? 'Expert tips and guides to help you understand and maintain your roof'
                : '专业提示和指南帮助您了解和维护您的屋顶'}
            </p>
          </div>
        </section>

        {/* Articles Grid */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Link */}
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-slate-600 hover:text-amber-600 mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {language === 'en' ? 'Back to Home' : '返回首页'}
            </Link>

            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/blog/${article.slug}`}
                    className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                  >
                    {/* Image */}
                    <div className="h-52 overflow-hidden">
                      <img
                        src={article.image_url}
                        alt={language === 'en' ? article.title_en : article.title_zh}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-amber-600 text-sm mb-3">
                        <BookOpen className="w-4 h-4" />
                        <span>{language === 'en' ? 'Article' : '文章'}</span>
                      </div>
                      <h2 className="text-xl font-semibold text-slate-900 mb-3 group-hover:text-amber-600 transition-colors">
                        {language === 'en' ? article.title_en : article.title_zh}
                      </h2>
                      <p className="text-slate-600 text-sm line-clamp-3 mb-4">
                        {language === 'en' ? article.summary_en : article.summary_zh}
                      </p>
                      <div className="flex items-center text-amber-600 font-medium text-sm">
                        {language === 'en' ? 'Read Article' : '阅读文章'}
                        <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </ContentContext.Provider>
  );
};

export default BlogListPage;
